import React,{useState,useEffect,useCallback}from'react';
import{useNavigate,useParams,useSearchParams}from'react-router-dom';
import Layout from'../components/layout/Layout';
import api from'../services/api';
import {useTranslation} from 'react-i18next';
import FileShaClaimButton from '../components/common/FileShaClaimButton';

export default function IPDTransferPage(){
  const {t}=useTranslation();
  const nav=useNavigate();
  const {admissionId}=useParams();
  const [loading,setLoading]=useState(true);
  const [sp]=useSearchParams();
  const aid=admissionId||sp.get('admissionId')||'';
  const [admission,setAdmission]=useState<any>(null);
  const [wards,setWards]=useState<any[]>([]);
  const [beds,setBeds]=useState<any[]>([]);
  const [form,setForm]=useState({wardId:'',bedId:'',transferType:'INTERNAL',reason:'',transferNotes:'',attendingDoctorId:'',clinicalSummary:'',vitalsSummary:'',pendingItems:''});
  const [saving,setSaving]=useState(false);
  const [msg,setMsg]=useState('');
  const f=(k:string,v:any)=>setForm(x=>({...x,[k]:v}));

  const load=useCallback(async()=>{
    try{
      setLoading(true);const [a,w]=await Promise.all([
        aid?api.getAdmissionById?.(aid).catch(()=>({data:null})):Promise.resolve({data:null}),
        api.getWards?.().catch(()=>({data:[]})),
      ]);
      setAdmission(a?.data);
      setWards(Array.isArray(w?.data)?w.data:w?.data?.data||[]);
    }catch{}
    setLoading(false);
  },[aid]);

  useEffect(()=>{load();},[load]);
  useEffect(()=>{
    if(!form.wardId)return;
    api.getBeds?.(form.wardId).then((r:any)=>setBeds(Array.isArray(r?.data)?r.data:r?.data?.data||[])).catch(()=>{});
  },[form.wardId]);

  const TRANSFER_TYPES=[
    {v:'INTERNAL',l:'Internal Transfer',desc:'Move to different ward/bed within same hospital'},
    {v:'ICU',l:'ICU Transfer',desc:'Transfer to Intensive Care Unit'},
    {v:'EXTERNAL',l:'External Referral',desc:'Transfer to another facility'},
    {v:'STEP_DOWN',l:'Step-Down',desc:'ICU to regular ward'},
  ];

  const submit=async()=>{
    if(!form.reason){setMsg('Transfer reason is required');return;}
    if(form.transferType!=='EXTERNAL'&&!form.bedId){setMsg('Please select a destination bed');return;}
    setSaving(true);
    try{
      await api.transferPatient?.(aid,form);
      nav('/ipd');
    }catch(e:any){setMsg('Error: '+(e.response?.data?.message||'Transfer failed'));}
    setSaving(false);
  };

  return(
    <Layout>
      <div className="p-6 max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div><h1 className="text-2xl font-bold text-gray-900">Patient Transfer</h1><p className="text-gray-500">Transfer patient to another ward, ICU, or facility</p></div>
          <div className="flex gap-2 items-center">
            {admission&&(
              <FileShaClaimButton
                patientId={admission.patient_id||admission.patientId}
                patientName={admission.patient_name||admission.patientName}
                encounterId={aid}
                serviceName="Inpatient transfer"
                serviceType="INPATIENT"
                admissionId={aid}
                from="ipd"
              />
            )}
            <button onClick={()=>nav('/ipd')} className="border px-4 py-2 rounded-lg hover:bg-gray-50 text-sm">← Back</button>
          </div>
        </div>
        {msg&&<div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg">{msg}</div>}

        {/* Current Admission */}
        {admission&&(
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
            <h3 className="font-semibold text-blue-800 mb-2">Current Location</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
              <div><p className="text-gray-500">{t('common.patient','Patient')}</p><p className="font-semibold">{admission.patient_name||admission.patientName||'—'}</p></div>
              <div><p className="text-gray-500">Ward / Bed</p><p className="font-semibold">{admission.ward_name||'—'} / Bed {admission.bed_number||'—'}</p></div>
              <div><p className="text-gray-500">Admitted</p><p className="font-semibold">{admission.admission_date?.split('T')[0]||'—'}</p></div>
            </div>
          </div>
        )}

        {/* Transfer Type */}
        <div className="bg-white rounded-xl shadow-sm border p-5 mb-4">
          <h3 className="font-bold text-gray-800 mb-4">Transfer Type</h3>
          <div className="grid grid-cols-2 gap-3">
            {TRANSFER_TYPES.map(t=>(
              <button key={t.v} onClick={()=>f('transferType',t.v)} className={`p-4 rounded-xl border-2 text-left transition-all ${form.transferType===t.v?'border-[#015CA6] bg-blue-50':'border-gray-200 hover:border-blue-300'}`}>
                <p className="font-semibold text-gray-800">{t.l}</p>
                <p className="text-xs text-gray-500 mt-1">{t.desc}</p>
              </button>
            ))}
          </div>
        </div>

        {/* Destination */}
        {form.transferType!=='EXTERNAL'&&(
          <div className="bg-white rounded-xl shadow-sm border p-5 mb-4">
            <h3 className="font-bold text-gray-800 mb-4">Destination</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Ward *</label>
                <select value={form.wardId} onChange={e=>f('wardId',e.target.value)} className="w-full border rounded-lg p-2">
                  <option value="">Select Ward</option>
                  {wards.map((w:any)=><option key={w.id} value={w.id}>{w.ward_name||w.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Available Bed *</label>
                <select value={form.bedId} onChange={e=>f('bedId',e.target.value)} className="w-full border rounded-lg p-2" disabled={!form.wardId}>
                  <option value="">Select Bed</option>
                  {beds.filter((b:any)=>b.status==='AVAILABLE').map((b:any)=><option key={b.id} value={b.id}>Bed {b.bed_number||b.name} — {b.bed_type||'General'}</option>)}
                </select>
                {form.wardId&&beds.filter((b:any)=>b.status==='AVAILABLE').length===0&&<p className="text-xs text-red-500 mt-1">No available beds in this ward</p>}
              </div>
            </div>
          </div>
        )}

        {form.transferType==='EXTERNAL'&&(
          <div className="bg-white rounded-xl shadow-sm border p-5 mb-4">
            <h3 className="font-bold text-gray-800 mb-4">Referral Facility</h3>
            <div><label className="block text-sm font-medium mb-1">Facility Name *</label><input onChange={e=>f('wardId',e.target.value)} className="w-full border rounded-lg p-2" placeholder="Name of referral hospital/facility"/></div>
          </div>
        )}

        {/* Clinical Summary */}
        <div className="bg-white rounded-xl shadow-sm border p-5 mb-6">
          <h3 className="font-bold text-gray-800 mb-4">Transfer Notes</h3>
          <div className="space-y-4">
            <div><label className="block text-sm font-medium mb-1">Transfer Reason *</label><select value={form.reason} onChange={e=>f('reason',e.target.value)} className="w-full border rounded-lg p-2"><option value="">Select reason</option>{['HIGHER_LEVEL_CARE','CLINICAL_DETERIORATION','SPECIALIST_CONSULTATION','BED_AVAILABILITY','PATIENT_REQUEST','STEP_DOWN','EXTERNAL_REFERRAL'].map(r=><option key={r}>{r}</option>)}</select></div>
            <div><label className="block text-sm font-medium mb-1">Clinical Summary</label><textarea value={form.clinicalSummary} onChange={e=>f('clinicalSummary',e.target.value)} className="w-full border rounded-lg p-2" rows={3} placeholder="Current condition, diagnosis, treatment given…"/></div>
            <div><label className="block text-sm font-medium mb-1">Current Vitals</label><input value={form.vitalsSummary} onChange={e=>f('vitalsSummary',e.target.value)} className="w-full border rounded-lg p-2" placeholder="BP 120/80, HR 78, SpO2 98%, Temp 98.6°F"/></div>
            <div><label className="block text-sm font-medium mb-1">Pending Items / Special Instructions</label><textarea value={form.pendingItems} onChange={e=>f('pendingItems',e.target.value)} className="w-full border rounded-lg p-2" rows={2} placeholder="Pending lab results, active IV lines, ongoing medications…"/></div>
          </div>
        </div>

        <div className="flex gap-3 justify-end">
          <button onClick={()=>nav('/ipd')} className="px-6 py-2.5 border rounded-xl hover:bg-gray-50">{t('common.cancel','Cancel')}</button>
          <button onClick={submit} disabled={saving} className="px-8 py-2.5 bg-[#02357A] text-white rounded-xl hover:bg-[#015CA6] font-semibold disabled:opacity-50">
            {saving?'Processing…':'Confirm Transfer →'}
          </button>
        </div>
      </div>
    </Layout>
  );
}
